import { ChevronRight } from "lucide-react";
import { PageState, SavedViewsBar, Skeleton, cn, type SavedView } from "@fox1ck-org/ui";

import { cardDelta } from "@/components/campaign/kpis";
import { MetricGrid, type MetricTile } from "@/components/common/metric-grid";
import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";
import { formatFilters, type ReportNode } from "@/lib/report";
import { narrow } from "@/lib/report-drill";
import { plural, useT } from "@/lib/i18n";
import { ReportFilterRow, ReportGrouping } from "./toolbar";
import { SPEND_WITHOUT_CLICKS } from "./names";
import type { ReportEngine } from "./engine";

/**
 * The phone's quick starts (доска MReport): what a buyer asks a report from
 * a phone, each a grouping of the same engine — by country then campaign, by
 * campaign then ad, by hour.
 */
const PHONE_STARTS: { id: string; dims: string[] }[] = [
  { id: "country", dims: ["country", "campaign"] },
  { id: "campaign", dims: ["campaign", "creative"] },
  { id: "hour", dims: ["hour"] },
];

/**
 * «Отчёт» at phone width (доска MReport): the one report engine with its
 * first level as a list. The grouping is the engine's (`dims` in the
 * address); a tap on a row narrows the report to it and opens the next level
 * — an ordinary filter (`flt`), so a desktop opened on the same link shows
 * the same slice. Every figure, «Итого» included, is GetBreakdownDrilldown's:
 * the tiles over the list are the desktop's «Итого» line.
 */
export function PhoneReport({ engine, starts = true }: { engine: ReportEngine; starts?: boolean }) {
  const t = useT();
  const { state, main, tree, totals, prevTotals, names } = engine;
  const dims = state.dims;
  const dim = dims[0]!;
  const deeper = dims.length > 1;
  const signed = (n: number | undefined) => (n === undefined || n === 0 ? "neutral" : n > 0 ? "ok" : "danger");
  const tiles: MetricTile[] = [
    { key: "clicks", label: t("cols.clicks"), value: formatInt(totals?.clicks), delta: cardDelta("clicks", "count", totals?.clicks, prevTotals?.clicks) },
    { key: "leads", label: t("cols.leads"), value: formatInt(totals?.leads), delta: cardDelta("leads", "count", totals?.leads, prevTotals?.leads) },
    {
      key: "profit",
      label: t("cols.profit"),
      value: formatMoney(totals?.profit, { signed: true }),
      delta: cardDelta("profit", "profit", totals?.profit, prevTotals?.profit),
      tone: signed(totals?.profit),
    },
  ];
  const views: SavedView[] = PHONE_STARTS.map((s) => ({ id: s.id, name: t(`adapt.report.start.${s.id}` as "adapt.report.start.country") }));
  const active = PHONE_STARTS.find((s) => s.dims.join(",") === dims.join(","))?.id ?? null;

  const open = (n: ReportNode) => {
    const value = n.path[n.path.length - 1]!;
    state.params.setFilters({ dims: dims.slice(1).join(","), flt: formatFilters(narrow(state.filters, dim, value)) || "-" });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto">
      {starts ? (
        <div className="-mx-3.5 shrink-0 overflow-x-auto px-3.5 [scrollbar-width:none] [&>*]:w-max [&>*]:flex-nowrap">
          <SavedViewsBar
            variant="chips"
            views={views}
            activeId={active}
            allLabel={null}
            onSelect={(v) => {
              const s = PHONE_STARTS.find((x) => x.id === v?.id);
              if (s) state.params.setFilters({ dims: s.dims.join(","), flt: null });
            }}
            labels={{ toolbar: t("adapt.report.starts") }}
          />
        </div>
      ) : null}
      <ReportGrouping engine={engine} compact className="shrink-0" filterTrigger={!state.filters.dims.length && !state.filters.metrics.length && !engine.pinned.length} />
      <ReportFilterRow engine={engine} className="shrink-0" />
      <MetricGrid tiles={tiles} loading={main.isLoading} label={t("adapt.report.total")} />
      <div className="flex shrink-0 items-center justify-between pt-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{engine.dimLabel(dim)}</h2>
        {SORT_WORD.has(state.sort.key) ? <span className="text-xs text-muted-foreground">{t(`adapt.sortBy.${state.sort.key}` as "adapt.sortBy.profit")}</span> : null}
      </div>
      {main.error && !main.data ? (
        <PageState mode="error" title={t("rep.error")} error={main.error} retry={() => void main.refetch()} />
      ) : main.isLoading || !main.data ? (
        <Skeleton className="h-64 w-full shrink-0 rounded-lg" />
      ) : tree.length === 0 ? (
        <p className="shrink-0 rounded-lg border border-border bg-card px-3.5 py-6 text-center text-sm text-muted-foreground">{t("rep.empty.period")}</p>
      ) : (
        <ul className="flex shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
          {tree.map((n) => {
            const value = n.path[n.path.length - 1] ?? "";
            const tappable = deeper && value !== SPEND_WITHOUT_CLICKS;
            const body = (
              <>
                <span className="flex min-w-0 flex-1 flex-col">
                  {names.pending(dim, value) ? (
                    <Skeleton className="my-0.5 h-3.5 w-40" aria-label={t("rep.naming")} />
                  ) : (
                    <span className="truncate text-sm font-medium">{names.name(dim, value)}</span>
                  )}
                  <span className="truncate font-mono text-xs text-muted-foreground">
                    {t("adapt.report.sub", { clicks: formatInt(n.v.clicks), leads: formatInt(n.v.leads), word: plural(t("word.leads"), n.v.leads ?? 0) })}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <b className={cn("font-mono text-sm font-semibold tabular-nums", tone(n.v.profit))}>{formatMoney(n.v.profit, { signed: true })}</b>
                  <span className={cn("font-mono text-xs tabular-nums", tone(n.v.roi))}>{n.v.roi === undefined ? DASH : formatPct(n.v.roi, { digits: 0, signed: true })}</span>
                </span>
                {tappable ? <ChevronRight className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} /> : null}
              </>
            );
            const cls = "flex min-h-13 w-full items-center gap-2.5 px-3.5 py-2 text-left";
            return (
              <li key={n.id} className="border-t border-border first:border-t-0">
                {tappable ? (
                  <button type="button" className={cn(cls, "hover:bg-surface-2")} onClick={() => open(n)}>
                    {body}
                  </button>
                ) : (
                  <div className={cls}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** The sorts the phone's words name («по профиту»). */
const SORT_WORD = new Set(["profit", "roi", "clicks", "leads", "cost", "name"]);

function tone(n: number | undefined): string {
  return (n ?? 0) > 0 ? "text-ok" : (n ?? 0) < 0 ? "text-destructive" : "text-muted-foreground";
}
